/**
 * A reply's activity, above its answer. Shut, it is one line: "Step 2 of 5: Run the tests"
 * with a progress bar, or what the reply did ("Read 4 files · ran 2 commands"); while it runs,
 * what it is doing now and for how long. Open, it is a rail: the plan's steps (TodoWrite) with
 * the calls made under each, or the model's rounds with their narration, each call one row
 * that opens to its template (templates.tsx). Thinking opens as "Thought for 6s".
 */
import { Brain, Check, ChevronRight, Circle, Hand, TriangleAlert } from '@orgus/ui/icons';
import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { AssistantEntry, AssistantLive } from '@orgus/protocol';
import { Badge, GROUP_INDENT, Markdown, Pressable, Spinner, WorkStateIcon, cn, formatDuration, formatInt, useNow } from '@orgus/ui';
import { phaseOf } from '../labels.ts';
import { describeSteps, groupSteps, nowDoing, planHeadline, spanMs, splitNarration } from '../turns.ts';
import type { PlanSection, Round, StepGroup, StepNode, Turn } from '../turns.ts';
import { TechnicalDetails, templateOf } from './templates.tsx';

// ---------------------------------------------------------------------------
// Rail
// ---------------------------------------------------------------------------

type NodeState = 'live' | 'current' | 'done' | 'failed' | 'waiting' | 'upcoming';

const NODE_LABEL: Record<NodeState, string> = { live: 'In progress', current: 'In progress', done: 'Done', failed: 'Some steps failed', waiting: 'Needs you', upcoming: 'Not started' };

function RailNode({ state }: { state: NodeState }) {
  const tile = 'flex size-4 shrink-0 items-center justify-center rounded-pill';
  const label = NODE_LABEL[state];
  switch (state) {
    case 'live':
      return (
        <span className={cn(tile, 'bg-status-working-soft text-status-working')} role="img" aria-label={label}>
          <Spinner size={10} />
        </span>
      );
    case 'current':
      return (
        <span className={cn(tile, 'bg-status-working-soft')} role="img" aria-label={label}>
          <span className="size-1.5 rounded-pill bg-status-working" />
        </span>
      );
    case 'done':
      return (
        <span className={cn(tile, 'bg-raised text-fg-muted')} role="img" aria-label={label}>
          <Check className="size-2.5" strokeWidth={3} />
        </span>
      );
    case 'failed':
      return (
        <span className={cn(tile, 'bg-status-danger-soft text-status-danger')} role="img" aria-label={label}>
          <TriangleAlert className="size-2.5" />
        </span>
      );
    case 'waiting':
      return (
        <span className={cn(tile, 'bg-status-attention-soft text-status-attention')} role="img" aria-label={label}>
          <Hand className="size-2.5" />
        </span>
      );
    case 'upcoming':
      return <span className={cn(tile, 'border border-border bg-bg')} role="img" aria-label={label} />;
  }
}

/** One stop on the rail: its node, a line down to the next, and what it holds. */
function RailItem({ state, last, children }: { state: NodeState; last: boolean; children: ReactNode }) {
  return (
    <li className="relative flex gap-3 pb-2">
      {!last && <span aria-hidden className="absolute bottom-0 left-2 top-5 w-px bg-border-subtle" />}
      <span className="pt-1">
        <RailNode state={state} />
      </span>
      <div className="min-w-0 flex-1">{children}</div>
    </li>
  );
}

const nodesState = (nodes: readonly StepNode[], live: boolean): NodeState => {
  if (nodes.some((n) => n.step.outcome === 'pending')) return 'waiting';
  if (nodes.some((n) => n.step.outcome === 'running')) return live ? 'live' : 'current';
  if (nodes.some((n) => n.step.outcome === 'error')) return 'failed';
  return 'done';
};

/** "3 actions · 1.2s" after a heading. */
function actionsMeta(nodes: readonly StepNode[], now: number): string {
  const parts: string[] = [];
  if (nodes.length > 1) parts.push(`${formatInt(nodes.length)} actions`);
  const ms = spanMs(nodes, now);
  if (ms !== null && ms >= 100 && !nodes.some((n) => n.step.outcome === 'running')) parts.push(formatDuration(ms));
  return parts.join(' · ');
}

// ---------------------------------------------------------------------------
// Rows
// ---------------------------------------------------------------------------

const REFUSED_CHIP: Record<string, string> = { denied: 'Denied', expired: 'Not approved in time' };

/** One call: its icon, its sentence, then a chevron that opens its template. */
export function ToolRow({ node, now }: { node: StepNode; now: number }) {
  const s = node.step;
  const t = templateOf(s);
  const phase = phaseOf(s);
  const [open, setOpen] = useState(false);
  const Icon = t.icon;
  const body = <t.View node={node} renderSteps={(ns) => <StepList nodes={ns} now={now} />} />;
  const broke = phase === 'failed';
  // One chip at most: waiting for a person, or why it did not run.
  const chip = phase === 'refused' ? (REFUSED_CHIP[s.outcome] ?? 'Not done') : null;
  return (
    <div className="flex flex-col" data-waiting={phase === 'waiting' || undefined}>
      <Pressable onClick={() => setOpen((o) => !o)} aria-expanded={open} className="group flex min-h-control-sm items-center gap-2 rounded-control px-1 text-left hover:bg-hover">
        {/* A 16 px icon slot, so a group's calls (one step in) start under the group's label. */}
        <span className="flex w-4 shrink-0 justify-center">
          {phase === 'running' ? (
            <Spinner size={14} className="shrink-0 text-status-working" />
          ) : phase === 'waiting' ? (
            <WorkStateIcon state="needs-you" />
          ) : (
            <Icon className={cn('size-3.5 shrink-0', broke ? 'text-status-danger' : 'text-fg-subtle')} aria-hidden />
          )}
        </span>
        <span className={cn('min-w-0 truncate text-caption', phase === 'running' || phase === 'waiting' ? 'text-fg' : 'text-fg-muted')}>
          {rowSentence(node)}
          {phase === 'running' && '…'}
        </span>
        {chip && (
          <Badge tone="neutral" size="sm" className="shrink-0">
            {chip}
          </Badge>
        )}
        {s.ms != null && s.ms >= 1000 && phase !== 'running' && <span className="shrink-0 text-caption text-fg-subtle num">{formatDuration(s.ms)}</span>}
        <ChevronRight className={cn('size-3.5 shrink-0 text-fg-subtle opacity-0 transition duration-(--duration-fast) group-hover:opacity-100 group-focus-visible:opacity-100', open && 'rotate-90 opacity-100')} aria-hidden />
      </Pressable>
      {open && (
        <div className="flex flex-col gap-2 pb-2 pl-4 pt-1 sm:pl-6">
          {s.message && phase !== 'done' && <p className="text-caption text-fg-muted">{s.message}</p>}
          {body}
          <TechnicalDetails step={s} />
        </div>
      )}
    </div>
  );
}

function rowSentence(node: StepNode): string {
  return groupSteps([node])[0]!.label;
}

/** Consecutive calls of one kind as one row ("Read 4 files"); opens to each call. */
function GroupRow({ group, now }: { group: StepGroup; now: number }) {
  const [open, setOpen] = useState(false);
  if (group.nodes.length === 1) return <ToolRow node={group.nodes[0]!} now={now} />;
  const t = templateOf(group.nodes[0]!.step);
  const Icon = t.icon;
  const running = group.nodes.some((n) => n.step.outcome === 'running');
  return (
    <div className="flex flex-col">
      <Pressable onClick={() => setOpen((o) => !o)} aria-expanded={open} className="group flex min-h-control-sm items-center gap-2 rounded-control px-1 text-left hover:bg-hover">
        <span className="flex w-4 shrink-0 justify-center">
          {running ? <Spinner size={14} className="shrink-0 text-status-working" /> : <Icon className={cn('size-3.5 shrink-0', group.failed ? 'text-status-danger' : 'text-fg-subtle')} aria-hidden />}
        </span>
        <span className="min-w-0 truncate text-caption text-fg-muted">
          {group.label}
          {running && '…'}
        </span>
        {group.times !== null && <span className="shrink-0 text-caption text-fg-subtle num">×{formatInt(group.times)}</span>}
        {group.failed > 0 && <span className="shrink-0 text-caption text-status-danger-ink">{formatInt(group.failed)} failed</span>}
        <ChevronRight className={cn('size-3.5 shrink-0 text-fg-subtle opacity-0 transition duration-(--duration-fast) group-hover:opacity-100 group-focus-visible:opacity-100', open && 'rotate-90 opacity-100')} aria-hidden />
      </Pressable>
      {open && (
        <div className={cn('flex flex-col', GROUP_INDENT)}>
          {group.nodes.map((n) => (
            <ToolRow key={n.entry.id} node={n} now={now} />
          ))}
        </div>
      )}
    </div>
  );
}

/** Calls as rows, like calls grouped. */
export function StepList({ nodes, now }: { nodes: StepNode[]; now: number }) {
  return (
    <div className="flex flex-col">
      {groupSteps(nodes).map((g) => (
        <GroupRow key={g.key} group={g} now={now} />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Thinking
// ---------------------------------------------------------------------------

/** "Thought for 6s": the model's own reasoning, shut by default. */
export function ThinkingDisclosure({ entries, live }: { entries: AssistantEntry[]; live?: string }) {
  const [open, setOpen] = useState(false);
  const text = [...entries.map((e) => e.text ?? ''), live ?? ''].filter(Boolean).join('\n\n');
  if (!text) return null;
  const ms = entries.reduce((a, e) => a + (e.ms ?? 0), 0);
  const label = live !== undefined && live ? 'Thinking' : ms >= 1000 ? `Thought for ${formatDuration(ms)}` : 'Thought';
  return (
    <div className="flex flex-col">
      <Pressable onClick={() => setOpen((o) => !o)} aria-expanded={open} className="flex min-h-control-sm items-center gap-2 self-start rounded-control px-1 text-caption text-fg-subtle hover:bg-hover hover:text-fg-muted">
        <Brain className="size-3.5" aria-hidden />
        {label}
        {live ? '…' : ''}
        <ChevronRight className={cn('size-3.5 transition-transform duration-(--duration-fast)', open && 'rotate-90')} aria-hidden />
      </Pressable>
      {open && <p className="ml-1 whitespace-pre-wrap border-l-2 border-border-subtle py-1 pl-3 text-caption text-fg-muted">{text}</p>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sections
// ---------------------------------------------------------------------------

/** A plan step: its node, title, action count, and the calls made under it. */
function PlanStep({ section, last, live, now }: { section: PlanSection; last: boolean; live: boolean; now: number }) {
  const holdsWait = section.items.some((n) => n.step.outcome === 'pending');
  const state: NodeState =
    section.state === 'done' ? (section.items.some((n) => n.step.outcome === 'error') ? 'failed' : 'done') : section.state === 'current' ? (holdsWait ? 'waiting' : live ? 'live' : 'current') : 'upcoming';
  const [open, setOpen] = useState(section.state === 'current' || holdsWait);
  const prev = useRef(section.state);
  useEffect(() => {
    if (section.state === 'current' && prev.current !== 'current') setOpen(true);
    prev.current = section.state;
  }, [section.state]);
  const meta = actionsMeta(section.items, now);
  return (
    <RailItem state={state} last={last}>
      <Pressable
        onClick={() => section.items.length && setOpen((o) => !o)}
        aria-expanded={section.items.length ? open : undefined}
        className="flex min-h-5 items-baseline gap-2 text-left text-caption"
        disabled={!section.items.length}
      >
        <span className={cn(section.state === 'current' ? 'font-medium text-fg' : section.state === 'done' ? 'text-fg-muted' : 'text-fg-subtle')}>
          {section.state === 'current' && live && section.activeForm ? section.activeForm : section.title}
        </span>
        {meta && <span className="shrink-0 text-fg-subtle num">{meta}</span>}
      </Pressable>
      {open && section.items.length > 0 && (
        <div className="mt-1">
          <StepList nodes={section.items} now={now} />
        </div>
      )}
    </RailItem>
  );
}

/** Calls outside the plan's steps: "Getting started", "Other work". */
function LooseSection({ title, nodes, last, live, now }: { title: string; nodes: StepNode[]; last: boolean; live: boolean; now: number }) {
  const [open, setOpen] = useState(false);
  return (
    <RailItem state={nodesState(nodes, live)} last={last}>
      <Pressable onClick={() => setOpen((o) => !o)} aria-expanded={open} className="flex min-h-5 items-baseline gap-2 text-left text-caption">
        <span className="text-fg-muted">{title}</span>
        <span className="text-fg-subtle num">{actionsMeta(nodes, now) || '1 action'}</span>
      </Pressable>
      {open && (
        <div className="mt-1">
          <StepList nodes={nodes} now={now} />
        </div>
      )}
    </RailItem>
  );
}

/** One model round: what it said first, its thinking, and its calls. */
function RoundItem({ round, index, count, last, live, now }: { round: Round; index: number; count: number; last: boolean; live: boolean; now: number }) {
  const { head, rest } = splitNarration(round.narration);
  const [more, setMore] = useState(false);
  const meta = actionsMeta(round.steps, now);
  const heading = head || (count > 1 ? describeSteps(round.steps) : '');
  return (
    <RailItem state={round.steps.length ? nodesState(round.steps, live) : 'done'} last={last}>
      {(heading || meta) && (
        <p className="flex min-h-5 flex-wrap items-baseline gap-x-2 text-caption">
          <span className={cn(live && last ? 'font-medium text-fg' : 'text-fg-muted')}>{heading || `Step ${index + 1}`}</span>
          {meta && <span className="text-fg-subtle num">{meta}</span>}
        </p>
      )}
      {rest && (
        <div className={cn('relative mt-1 overflow-hidden', !more && 'max-h-16')}>
          <Markdown source={rest} size="sm" className="text-caption text-fg-muted" />
          {!more && rest.length > 240 && (
            <Pressable onClick={() => setMore(true)} className="absolute inset-x-0 bottom-0 bg-surface text-left text-caption text-accent-text hover:underline">
              Show more
            </Pressable>
          )}
        </div>
      )}
      {round.thinking.length > 0 && <ThinkingDisclosure entries={round.thinking} />}
      {round.steps.length > 0 && <StepList nodes={round.steps} now={now} />}
    </RailItem>
  );
}

// ---------------------------------------------------------------------------
// The timeline
// ---------------------------------------------------------------------------

/** The mini progress bar beside a plan's headline. */
function PlanBar({ done, total }: { done: number; total: number }) {
  return (
    <span className="h-1 w-12 shrink-0 overflow-hidden rounded-pill bg-active" role="progressbar" aria-label="Plan progress" aria-valuemin={0} aria-valuemax={total} aria-valuenow={done}>
      <span className="motion-segment block h-full w-full rounded-pill bg-fg-muted" style={{ scale: `${total ? done / total : 0} 1` }} />
    </span>
  );
}

/** Whether a turn has anything to show above its answer. */
export function hasActivity(turn: Turn): boolean {
  return turn.steps.length > 0 || turn.rounds.some((r) => r.thinking.length > 0 || r.narration);
}

export function Activity({ turn, live }: { turn: Turn; live: AssistantLive | null }) {
  const now = useNow(1000);
  const isLive = turn.live;
  const waiting = turn.waiting.length > 0;
  const [open, setOpen] = useState<boolean>(isLive || waiting);
  const wasLive = useRef(isLive);
  useEffect(() => {
    // Open while it runs and when something starts waiting; shut once it is done.
    if (isLive && !wasLive.current) setOpen(true);
    if (!isLive && wasLive.current && !waiting) setOpen(false);
    wasLive.current = isLive;
  }, [isLive, waiting]);
  useEffect(() => {
    if (waiting) setOpen(true);
  }, [waiting]);

  const plan = turn.plan;
  const thinkingOnly = !turn.steps.length && turn.rounds.every((r) => !r.narration);
  const started = turn.reply?.startedAt ?? turn.user?.at ?? turn.at;
  const elapsed = isLive ? now - started : null;

  if (thinkingOnly && !isLive) {
    const thoughts = turn.rounds.flatMap((r) => r.thinking);
    return thoughts.length ? <ThinkingDisclosure entries={thoughts} /> : null;
  }

  const headline = plan ? planHeadline(plan, isLive) : isLive ? `${nowDoing(turn, live)}…` : describeSteps(turn.steps);
  const doing = plan && isLive ? nowDoing(turn, live) : null;
  const failedUnrecovered = !isLive && turn.errors.length > 0;

  return (
    <section className={cn('flex flex-col gap-1 rounded-card', open && 'bg-surface pb-2 shadow-elevation-flat')} aria-label="What the assistant did">
      <Pressable onClick={() => setOpen((o) => !o)} aria-expanded={open} className="flex min-h-control items-center gap-2 rounded-card px-3 text-left text-caption hover:bg-hover">
        {isLive ? <Spinner size={12} className="shrink-0 text-status-working" /> : <ChevronRight className={cn('size-3.5 shrink-0 text-fg-subtle transition-transform duration-(--duration-fast)', open && 'rotate-90')} aria-hidden />}
        <span className={cn('min-w-0 truncate', failedUnrecovered ? 'text-status-danger-ink' : isLive ? 'text-fg' : 'text-fg-muted')}>{headline}</span>
        {plan && <PlanBar done={plan.done} total={plan.total} />}
        {doing && !waiting && <span className="hidden min-w-0 truncate text-fg-subtle md:inline">· {doing}…</span>}
        {elapsed !== null && elapsed >= 3000 && <span className="ml-auto shrink-0 text-fg-subtle num">{formatDuration(elapsed)}</span>}
      </Pressable>
      {open && (
        <ol className="motion-arrive flex flex-col px-3 pt-1" aria-label={plan ? 'Plan' : 'Steps'}>
          {plan ? (
            <>
              {turn.rounds.some((r) => r.thinking.length) && (
                <li className="pb-1">
                  <ThinkingDisclosure entries={turn.rounds.flatMap((r) => r.thinking)} />
                </li>
              )}
              {plan.before.length > 0 && <LooseSection title="Getting started" nodes={plan.before} last={false} live={isLive} now={now} />}
              {plan.sections.map((s, i) => (
                <PlanStep key={s.key} section={s} last={i === plan.sections.length - 1 && !plan.other.length} live={isLive} now={now} />
              ))}
              {plan.other.length > 0 && <LooseSection title="Other work" nodes={plan.other} last live={isLive} now={now} />}
            </>
          ) : (
            turn.rounds.map((r, i) => <RoundItem key={r.key} round={r} index={i} count={turn.rounds.length} last={i === turn.rounds.length - 1} live={isLive} now={now} />)
          )}
          {isLive && live?.thinking && (
            <li className="pl-8">
              <ThinkingDisclosure entries={[]} live={live.thinking} />
            </li>
          )}
        </ol>
      )}
    </section>
  );
}
